import { createCryptoCurrency } from '@0xtorch/core'
import { beam as viemBeam } from 'viem/chains'
import { type Client, createClient } from '../../client'
import type { Explorer } from '../../explorer'
import { createRoutescan } from '../../explorer'
import type { LowerHex } from '../../types'
import type { Chain } from '../types/chain'
import type { HttpRpc } from '../types/rpc'

/**
 * Beam のネイティブトークンとして利用する BEAM 通貨定義。
 */
const beam = createCryptoCurrency({
  type: 'CryptoCurrency',
  id: 'beam-2',
  name: 'Beam',
  symbol: 'BEAM',
  market: {
    coingeckoId: 'beam-2',
  },
  updatedAt: 0,
})

/**
 * Beam チェーン定義を作成する。
 *
 * @param params.explorerApiKey - 任意の Routescan API key
 * @param params.explorerProxyUrl - Explorer API を中継するプロキシ URL
 * @param params.explorerPageSize - account 系 Explorer API のページサイズ
 * @returns Beam の EVM チェーン定義
 */
export const createBeamChain = ({
  explorerApiKey: apiKey,
  explorerProxyUrl: proxyUrl,
  explorerPageSize: pageSize,
}: {
  explorerApiKey?: string
  explorerProxyUrl?: string
  explorerPageSize?: number
}): Chain =>
  createBeamChainCustom({
    client: createClient({
      chain: viemBeam,
      httpRpcs: beamHttpRpcs,
    }),
    explorer: createRoutescan({
      name: 'Beam Explorer',
      baseUrl: 'https://4337.snowtrace.io',
      chainId: 4337,
      apiKey,
      proxyUrl,
      pageSize,
    }),
  })

type CreateBeamChainParameters = {
  client: Client
  explorer: Explorer
}

/**
 * Beam チェーン定義を任意の client / explorer で作成する。
 *
 * @param params.client - EVM RPC クライアント
 * @param params.explorer - Explorer クライアント
 * @returns Beam の EVM チェーン定義
 */
export const createBeamChainCustom = ({
  client,
  explorer,
}: CreateBeamChainParameters): Chain => ({
  id: 4337,
  network: 'beam',
  name: 'Beam',
  nativeToken: {
    name: 'Beam',
    symbol: 'BEAM',
    decimals: 18,
    currency: beam,
  },
  wrappedTokenAddresses: new Set<LowerHex>([
    '0xd51bfa777609213a653a2cd067c9a0132a2d316a',
  ]),
  explorer,
  client,
  blockTime: undefined,
  coingeckoId: 'beam',
  defillamaId: 'beam',
})

/**
 * Beam の HTTP RPC 候補。
 */
export const beamHttpRpcs: HttpRpc[] = [
  {
    url: 'https://build.onbeam.com/rpc',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 2048n,
  },
  {
    url: 'https://subnets.avax.network/beam/mainnet/rpc',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 2048n,
  },
]

/**
 * Beam の WebSocket RPC 候補。
 */
export const beamWebsocketRpcUrls: readonly string[] = [
  'wss://build.onbeam.com/ws',
  'wss://subnets.avax.network/beam/mainnet/ws',
]
